import Phaser from "phaser";
import { collectItem, createInitialGameState, resetGame } from "../game/state";
import type { Collectible, GameState, InputAction, OutfitStage } from "../game/types";
import { createHud, type Hud } from "../game/hud";

const world = {
  width: 1024,
  height: 640,
  playerSpeed: 210
};

const outfitGlowColors: Record<OutfitStage, number> = {
  ripped: 0x8e725c,
  tidy: 0xffa2c5,
  sparkly: 0x83dbe7,
  royal: 0xffd85e
};

export class GardenScene extends Phaser.Scene {
  private state: GameState = createInitialGameState();
  private hud!: Hud;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<InputAction, Phaser.Input.Keyboard.Key>;
  private player!: Phaser.GameObjects.Container;
  private outfitGlow!: Phaser.GameObjects.Ellipse;
  private playerSprite!: Phaser.GameObjects.Image;
  private collectibles = new Map<string, Phaser.GameObjects.Container>();
  private touchActions = new Set<InputAction>();

  constructor() {
    super("GardenScene");
  }

  preload(): void {
    this.load.image("concept", "/assets/generated/magic-world-concept.png");
    this.load.image("princess-elodie", "/assets/generated/sprites/princess-elodie.png");
    this.load.svg("candy-lollipop", "/assets/generated/sprites/candy-lollipop.svg", { width: 96, height: 96 });
    this.load.svg("candy-cupcake", "/assets/generated/sprites/candy-cupcake.svg", { width: 96, height: 96 });
    this.load.svg("candy-wrapped", "/assets/generated/sprites/candy-wrapped.svg", { width: 96, height: 96 });
    this.load.svg("candy-cookie", "/assets/generated/sprites/candy-cookie.svg", { width: 96, height: 96 });
    this.load.svg("clothes-shoes", "/assets/generated/sprites/clothes-shoes.svg", { width: 96, height: 96 });
    this.load.svg("clothes-ribbon", "/assets/generated/sprites/clothes-ribbon.svg", { width: 96, height: 96 });
    this.load.svg("clothes-skirt", "/assets/generated/sprites/clothes-skirt.svg", { width: 96, height: 96 });
    this.load.svg("clothes-cape", "/assets/generated/sprites/clothes-cape.svg", { width: 96, height: 96 });
    this.load.svg("clothes-crown", "/assets/generated/sprites/clothes-crown.svg", { width: 96, height: 96 });
    this.load.svg("magic-mirror", "/assets/generated/sprites/magic-mirror.svg", { width: 128, height: 160 });
    this.load.svg("royal-wardrobe", "/assets/generated/sprites/royal-wardrobe.svg", { width: 160, height: 160 });
    this.load.svg("rose-cluster", "/assets/generated/sprites/rose-cluster.svg", { width: 128, height: 96 });
    this.load.svg("sparkle", "/assets/generated/sprites/sparkle.svg", { width: 64, height: 64 });
  }

  create(): void {
    this.hud = createHud();
    this.state = createInitialGameState();
    this.createGarden();
    this.createCollectibles();
    this.createPlayer();
    this.createControls();
    this.hud.render(this.state);
    this.hud.announce("Hello Elodie", "Collect 8 candies and 5 clothes, then visit the mirror.");
  }

  update(_time: number, delta: number): void {
    const direction = this.resolveMoveDirection();
    const dt = delta / 1000;

    this.player.x = Phaser.Math.Clamp(
      this.player.x + direction.x * world.playerSpeed * dt,
      54,
      world.width - 54
    );
    this.player.y = Phaser.Math.Clamp(
      this.player.y + direction.y * world.playerSpeed * dt,
      78,
      world.height - 54
    );

    if (direction.x !== 0 || direction.y !== 0) {
      this.player.setScale(direction.x < 0 ? -1 : 1, 1);
    }

    this.checkCollection();
    this.checkFinish();
  }

  private createGarden(): void {
    const graphics = this.add.graphics();

    graphics.fillGradientStyle(0xbef1c7, 0xbef1c7, 0x90d8b4, 0x90d8b4, 1);
    graphics.fillRect(0, 0, world.width, world.height);

    graphics.fillStyle(0xf8d77e, 1);
    graphics.fillEllipse(512, 355, 780, 150);
    graphics.fillEllipse(512, 355, 720, 112);

    graphics.lineStyle(4, 0xffffff, 0.45);
    graphics.strokeEllipse(512, 355, 780, 150);

    for (const flowers of gardenFlowers) {
      this.add
        .image(flowers.x, flowers.y, "rose-cluster")
        .setDisplaySize(flowers.width, flowers.height)
        .setAngle(flowers.angle)
        .setAlpha(flowers.alpha)
        .setDepth(1);
    }

    this.add.image(892, 506, "concept").setDisplaySize(178, 112).setAlpha(0.82).setDepth(0);
    this.add
      .rectangle(892, 506, 188, 122, 0xffffff, 0.28)
      .setStrokeStyle(2, 0xffffff, 0.72)
      .setDepth(0);

    this.drawSparkles();
    this.drawMirror(884, 286);
    this.drawWardrobe(104, 126);
  }

  private createPlayer(): void {
    this.player = this.add.container(512, 330).setDepth(20);

    const shadow = this.add.ellipse(-2, 42, 64, 18, 0x4b7a56, 0.28);
    this.outfitGlow = this.add.ellipse(0, 4, 82, 124, outfitGlowColors.ripped, 0.1);
    this.playerSprite = this.add
      .image(0, -18, "princess-elodie")
      .setDisplaySize(112, 168)
      .setOrigin(0.5, 0.76);
    const sparkle = this.add.image(32, -82, "sparkle").setDisplaySize(24, 24).setAlpha(0.86);

    this.player.add([shadow, this.outfitGlow, this.playerSprite, sparkle]);
    this.refreshPlayerOutfit();

    this.tweens.add({
      targets: sparkle,
      scaleX: sparkle.scaleX * 1.18,
      scaleY: sparkle.scaleY * 1.18,
      alpha: 0.36,
      angle: 35,
      duration: 900,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1
    });
  }

  private createCollectibles(): void {
    for (const item of this.state.collectibles) {
      const sprite =
        item.kind === "candy" ? this.createCandySprite(item) : this.createClothesSprite(item);

      this.collectibles.set(item.id, sprite);
    }
  }

  private createCandySprite(item: Collectible): Phaser.GameObjects.Container {
    const container = this.add.container(item.position.x, item.position.y).setDepth(10);
    const wrapper = this.add.circle(0, 0, 25, 0xffffff, 0.62);
    const candy = this.add
      .image(0, 0, resolveCandyTexture(item))
      .setDisplaySize(item.label === "Cupcake" ? 48 : 54, item.label === "Cupcake" ? 48 : 54);
    const glint = this.add.image(19, -21, "sparkle").setDisplaySize(18, 18).setAlpha(0.7);
    const label = this.add.text(0, 28, item.label, itemTextStyle).setOrigin(0.5, 0);

    container.add([wrapper, candy, glint, label]);
    this.addBob(container, item.id);
    this.addTwinkle(glint, item.id.length * 80);
    return container;
  }

  private createClothesSprite(item: Collectible): Phaser.GameObjects.Container {
    const container = this.add.container(item.position.x, item.position.y).setDepth(10);
    const glow = this.add.circle(0, 0, 28, 0xfff0a8, 0.62);
    const sprite = this.add.image(0, 0, resolveClothesTexture(item)).setDisplaySize(58, 58);
    const sparkle = this.add.image(21, -20, "sparkle").setDisplaySize(18, 18).setAlpha(0.86);
    const label = this.add.text(0, 30, item.label, itemTextStyle).setOrigin(0.5, 0);

    container.add([glow, sprite, sparkle, label]);
    this.addBob(container, item.id);
    this.addTwinkle(sparkle, item.id.length * 70);
    return container;
  }

  private createControls(): void {
    if (!this.input.keyboard) {
      throw new Error("Keyboard input is unavailable");
    }

    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = {
      up: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D)
    };
    this.input.keyboard.on("keydown", (event: KeyboardEvent) => {
      const action = resolveActionFromKey(event.key);

      if (action) {
        this.nudgePlayer(action);
      }
    });

    document.querySelectorAll<HTMLButtonElement>("[data-dir]").forEach((button) => {
      const action = button.dataset.dir as InputAction;

      button.addEventListener("pointerdown", () => {
        this.touchActions.add(action);
        this.nudgePlayer(action);
      });
      button.addEventListener("pointerup", () => this.touchActions.delete(action));
      button.addEventListener("pointerleave", () => this.touchActions.delete(action));
      button.addEventListener("pointercancel", () => this.touchActions.delete(action));
    });
  }

  private resolveMoveDirection(): Phaser.Math.Vector2 {
    const direction = new Phaser.Math.Vector2(0, 0);

    if (this.cursors.left.isDown || this.wasd.left.isDown || this.touchActions.has("left")) {
      direction.x -= 1;
    }
    if (this.cursors.right.isDown || this.wasd.right.isDown || this.touchActions.has("right")) {
      direction.x += 1;
    }
    if (this.cursors.up.isDown || this.wasd.up.isDown || this.touchActions.has("up")) {
      direction.y -= 1;
    }
    if (this.cursors.down.isDown || this.wasd.down.isDown || this.touchActions.has("down")) {
      direction.y += 1;
    }

    return direction.normalize();
  }

  private checkCollection(): void {
    for (const [id, sprite] of this.collectibles) {
      if (!sprite.active) {
        continue;
      }

      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, sprite.x, sprite.y);

      if (distance <= 48) {
        const beforeStage = this.state.outfitStage;
        this.state = collectItem(this.state, id);
        sprite.setActive(false);
        this.tweens.add({
          targets: sprite,
          y: sprite.y - 32,
          alpha: 0,
          scale: 1.35,
          duration: 260,
          ease: "Sine.easeOut",
          onComplete: () => sprite.destroy()
        });
        this.refreshPlayerOutfit();
        this.hud.render(this.state);
        this.announceCollection(id, beforeStage);
      }
    }
  }

  private announceCollection(id: string, beforeStage: OutfitStage): void {
    const item = this.state.collectibles.find((candidate) => candidate.id === id);

    if (!item) {
      return;
    }

    if (item.kind === "clothes" && beforeStage !== this.state.outfitStage) {
      this.hud.announce("New outfit", `The dress is now ${this.state.outfitStage}.`);
      this.cameras.main.flash(160, 255, 246, 178);
      return;
    }

    this.hud.announce(item.kind === "candy" ? "Sweet find" : "Pretty find", item.label);
  }

  private checkFinish(): void {
    const atMirror = Phaser.Math.Distance.Between(this.player.x, this.player.y, 884, 286) <= 70;

    if (this.state.complete && atMirror) {
      this.hud.announce("Princess ready", "Elodie made the garden bright and beautiful.");
      this.time.delayedCall(900, () => {
        this.state = resetGame();
        this.scene.restart();
      });
    } else if (this.state.complete) {
      this.hud.announce("Almost ready", "Visit the mirror to finish the makeover.");
    }
  }

  private drawMirror(x: number, y: number): void {
    this.add.image(x, y + 18, "magic-mirror").setDisplaySize(92, 116).setDepth(4);
    this.add.text(x, y + 93, "Mirror", itemTextStyle).setOrigin(0.5, 0);
  }

  private drawWardrobe(x: number, y: number): void {
    this.add.image(x, y, "royal-wardrobe").setDisplaySize(122, 122).setDepth(3);
    this.add.text(x, y + 68, "Closet", itemTextStyle).setOrigin(0.5, 0);
  }

  private addBob(target: Phaser.GameObjects.Container, key: string): void {
    this.tweens.add({
      targets: target,
      y: target.y + 8,
      duration: 900 + key.length * 23,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1
    });
  }

  private addTwinkle(target: Phaser.GameObjects.Image, delay: number): void {
    const baseScaleX = target.scaleX;
    const baseScaleY = target.scaleY;

    this.tweens.add({
      targets: target,
      scaleX: baseScaleX * 1.35,
      scaleY: baseScaleY * 1.35,
      alpha: 0.18,
      angle: 60,
      delay,
      duration: 720,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1
    });
  }

  private drawSparkles(): void {
    for (const sparkle of gardenSparkles) {
      const sprite = this.add
        .image(sparkle.x, sparkle.y, "sparkle")
        .setDisplaySize(sparkle.size, sparkle.size)
        .setAlpha(sparkle.alpha)
        .setDepth(2);

      this.addTwinkle(sprite, sparkle.delay);
    }
  }

  private refreshPlayerOutfit(): void {
    const stage = this.state.outfitStage;
    const alpha: Record<OutfitStage, number> = {
      ripped: 0.08,
      tidy: 0.16,
      sparkly: 0.24,
      royal: 0.32
    };

    this.outfitGlow.setFillStyle(outfitGlowColors[stage], alpha[stage]);
    this.playerSprite.clearTint();

    if (stage === "tidy") {
      this.playerSprite.setTint(0xfff0f6);
    } else if (stage === "sparkly") {
      this.playerSprite.setTint(0xe8fcff);
    } else if (stage === "royal") {
      this.playerSprite.setTint(0xfff3b8);
    }
  }

  private nudgePlayer(action: InputAction): void {
    const step = 26;
    const direction = actionVectors[action];

    this.player.x = Phaser.Math.Clamp(this.player.x + direction.x * step, 54, world.width - 54);
    this.player.y = Phaser.Math.Clamp(this.player.y + direction.y * step, 78, world.height - 54);
    this.player.setScale(direction.x < 0 ? -1 : 1, 1);
    this.checkCollection();
    this.checkFinish();
  }
}

const itemTextStyle: Phaser.Types.GameObjects.Text.TextStyle = {
  color: "#553723",
  fontFamily: "Georgia, serif",
  fontSize: "14px",
  fontStyle: "bold",
  stroke: "#fff8d8",
  strokeThickness: 4
};

const gardenFlowers = [
  { x: 78, y: 252, width: 118, height: 86, angle: -14, alpha: 0.96 },
  { x: 164, y: 90, width: 98, height: 72, angle: 8, alpha: 0.9 },
  { x: 244, y: 562, width: 108, height: 78, angle: 12, alpha: 0.94 },
  { x: 374, y: 548, width: 98, height: 72, angle: -8, alpha: 0.88 },
  { x: 466, y: 104, width: 100, height: 74, angle: 5, alpha: 0.9 },
  { x: 724, y: 92, width: 104, height: 76, angle: -6, alpha: 0.92 },
  { x: 862, y: 104, width: 116, height: 84, angle: 12, alpha: 0.95 },
  { x: 936, y: 408, width: 104, height: 76, angle: -10, alpha: 0.9 },
  { x: 792, y: 552, width: 118, height: 86, angle: 7, alpha: 0.95 },
  { x: 594, y: 576, width: 104, height: 76, angle: -5, alpha: 0.9 },
  { x: 72, y: 504, width: 108, height: 78, angle: 10, alpha: 0.9 },
  { x: 952, y: 248, width: 96, height: 70, angle: -12, alpha: 0.86 }
];

const gardenSparkles = [
  { x: 336, y: 230, size: 22, alpha: 0.78, delay: 60 },
  { x: 528, y: 188, size: 18, alpha: 0.62, delay: 280 },
  { x: 712, y: 210, size: 26, alpha: 0.72, delay: 520 },
  { x: 810, y: 388, size: 20, alpha: 0.68, delay: 190 },
  { x: 220, y: 392, size: 18, alpha: 0.58, delay: 760 },
  { x: 612, y: 518, size: 24, alpha: 0.66, delay: 410 }
];

const actionVectors: Record<InputAction, { x: number; y: number }> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 }
};

function resolveActionFromKey(key: string): InputAction | undefined {
  switch (key) {
    case "ArrowUp":
    case "w":
    case "W":
      return "up";
    case "ArrowDown":
    case "s":
    case "S":
      return "down";
    case "ArrowLeft":
    case "a":
    case "A":
      return "left";
    case "ArrowRight":
    case "d":
    case "D":
      return "right";
    default:
      return undefined;
  }
}

function resolveCandyTexture(item: Collectible): string {
  switch (item.label) {
    case "Cupcake":
      return "candy-cupcake";
    case "Cookie":
      return "candy-cookie";
    case "Jellybean":
    case "Mint":
    case "Bonbon":
    case "Berry Pop":
      return "candy-wrapped";
    case "Lolly":
    case "Taffy":
    default:
      return "candy-lollipop";
  }
}

function resolveClothesTexture(item: Collectible): string {
  switch (item.label) {
    case "Clean Shoes":
      return "clothes-shoes";
    case "Ribbon":
      return "clothes-ribbon";
    case "Sparkle Skirt":
      return "clothes-skirt";
    case "Royal Cape":
      return "clothes-cape";
    case "Crown":
    default:
      return "clothes-crown";
  }
}
